import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { EXCEL_COMMISSION_DETAILS_V3_LABELS } from "../constants/excelFieldKeys";

export type CommissionDetailsExportRow = {
  commissionType: string;
  commissionPercent: string;
  payable: number;
  paid: number;
};

export type CommissionDetailsExportData = {
  invoiceNumber: string;
  invoiceAmount: number;
  shippingCharges: number;
  creditAmount: number;
  lateFees: number;
  invoiceTotal: number;
  /** Profit Summary invoice total. Defaults to invoiceTotal. Non-P&B uses the MFG invoice amount. */
  profitInvoiceTotal?: number;
  purchaseAndBillEnabled: boolean;
  cogsAmount: number;
  shippingChargesActual: number;
  v3TotalCost: number;
  v3ServiceFeeTotal?: number;
  agencyCommissionAmount: number;
  overrideCommissionAmount: number;
  finalProfit: number;
  commissionRows: CommissionDetailsExportRow[];
};

const roundCurrency = (value: number) => Math.round(value * 100) / 100;

const addLabelValueRow = (worksheet: ExcelJS.Worksheet, label: string, value: string | number) => {
  worksheet.addRow([label, value]);
};

export const exportCommissionDetailsToExcel = async (data: CommissionDetailsExportData) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Service Fee Details");

  worksheet.getColumn(1).width = 32;
  worksheet.getColumn(2).width = 18;
  worksheet.getColumn(3).width = 14;
  worksheet.getColumn(4).width = 14;

  worksheet.addRow(["Invoice", `#${data.invoiceNumber}`]);
  worksheet.addRow([]);

  addLabelValueRow(worksheet, "Invoice Amount", roundCurrency(data.invoiceAmount));

  if (data.creditAmount > 0) {
    addLabelValueRow(worksheet, "Credit Amount", roundCurrency(-data.creditAmount));
  }
  if (data.shippingCharges > 0) {
    addLabelValueRow(worksheet, "Shipping Charges", roundCurrency(data.shippingCharges));
  }
  if (data.lateFees > 0) {
    addLabelValueRow(worksheet, "Late Fees", roundCurrency(data.lateFees));
  }

  addLabelValueRow(worksheet, "Invoice Total", roundCurrency(data.invoiceTotal));
  worksheet.addRow([]);

  worksheet.addRow([EXCEL_COMMISSION_DETAILS_V3_LABELS.COSTS_SECTION]);
  addLabelValueRow(worksheet, "COGs", roundCurrency(data.purchaseAndBillEnabled ? data.cogsAmount : 0));
  addLabelValueRow(worksheet, "Shipping Charges (Actual)", roundCurrency(data.shippingChargesActual));
  addLabelValueRow(worksheet, EXCEL_COMMISSION_DETAILS_V3_LABELS.TOTAL_COST, roundCurrency(data.v3TotalCost));
  worksheet.addRow([]);

  if (data.commissionRows.length > 0) {
    worksheet.addRow(["Service Fee Type", "Service Fee %", "Payable", "Paid"]);
    data.commissionRows.forEach((row) => {
      worksheet.addRow([row.commissionType, row.commissionPercent, roundCurrency(row.payable), roundCurrency(row.paid)]);
    });
    worksheet.addRow([]);
  }

  worksheet.addRow(["Profit"]);
  addLabelValueRow(worksheet, "Invoice Total", roundCurrency(data.profitInvoiceTotal ?? data.invoiceTotal));
  if (!data.purchaseAndBillEnabled) {
    addLabelValueRow(worksheet, "V3 Service Fee Total", roundCurrency(data.v3ServiceFeeTotal ?? 0));
  }
  addLabelValueRow(worksheet, EXCEL_COMMISSION_DETAILS_V3_LABELS.LESS_TOTAL_COST, roundCurrency(-data.v3TotalCost));
  addLabelValueRow(worksheet, "Less: Agency Service Fee", roundCurrency(-data.agencyCommissionAmount));
  if (data.overrideCommissionAmount > 0) {
    addLabelValueRow(worksheet, "Less: OVR Service Fee", roundCurrency(-data.overrideCommissionAmount));
  }
  addLabelValueRow(worksheet, "Profit", roundCurrency(data.finalProfit));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const sanitizedInvoiceNumber = data.invoiceNumber.replace(/[^a-zA-Z0-9-_]/g, "");
  saveAs(blob, `service-fee-details-${sanitizedInvoiceNumber}.xlsx`);
};
